import React, { PureComponent } from 'react';
import { Form, Button, Icon, Dimmer, Loader } from 'semantic-ui-react';
import PatternEditor from 'widgets/bussiness/pattern-editor';
import { ItemReference, QueryResult } from 'stores/dataStore';
import { PatternRowDto } from 'stores/configuration/machinery/pattern-rows-store';
import { WithTranslation, withTranslation } from 'react-i18next';
import { HierarchyDto, HierarchyStore, IdNameDto, PatternsStore } from 'stores/configuration/machinery/patterns-store';
import MachineListItem, { MachineListItemViewModel } from 'site/pages/shared/events-and-requests/machine-list-item';
import { connect } from 'redux-scaffolding-ts';
import './pattern-machines.less';
import { customEqualCompareBystringify, isNullOrWhiteSpaces } from 'utils/useful-functions';
import { nameof } from 'utils/object';
import { ClusterDto, DropDownClustersStore } from 'stores/configuration/machinery/clusters-store';
import { EquipmentTypeDto, EquipmentTypesStore } from 'stores/configuration/machinery/equipment-types-store';
import { EquipmentTypeItemReference } from 'widgets/bussiness/equipment-type-editor';

export interface PatternMachinesViewModel {
  pattern: ItemReference;
  machines: MachineListItemViewModel[];
}

export interface PatternMachinesProps extends WithTranslation {
  readOnly: boolean;
  showRequired?: boolean;
  patternsStore?: PatternsStore;
  hierarchyStore?: HierarchyStore;
  value: PatternMachinesViewModel;
  onChange?: (value: PatternMachinesViewModel) => void;
  className?: string;
  loadPatternsOnOpen?: boolean;
  clusters?: DropDownClustersStore;
  equipmentTypes?: EquipmentTypesStore;
  profession: ItemReference;
  warningMessageEnabled?: boolean;
}

export interface PatternMachinesState {
  hasPattern: boolean;
  patternRows: PatternRowDto[];
  clusters?: QueryResult<ClusterDto>;
  equipmentTypes?: QueryResult<EquipmentTypeDto>;
  someMachineDeleted: boolean;
  loading: boolean;
}

@connect(
  ['patternsStore', PatternsStore],
  ['hierarchyStore', HierarchyStore],
  ['clusters', DropDownClustersStore],
  ['equipmentTypes', EquipmentTypesStore]
)
class PatternMachines extends PureComponent<PatternMachinesProps, PatternMachinesState> {
  static defaultProps = { onChange: () => {} };
  state: PatternMachinesState = {
    patternRows: [],
    hasPattern: false,
    someMachineDeleted: false,
    loading: false
  };

  private get clustersStore() {
    return this.props.clusters;
  }

  private get equipmentTypesStore() {
    return this.props.equipmentTypes;
  }

  componentDidMount() {
    if (!this.props.value.machines || this.props.value.machines.length === 0) {
      this.props.onChange({
        pattern: null,
        machines: [{ cluster: null, equipmentType: null, oem: null, machineModel: null, machineUnits: [], plcTypes: [] }]
      });
    } else {
      const machinesProp = this.props.value.machines;
      let model: HierarchyDto = {
        clusterIds: machinesProp.flatMap(x => x.cluster?.id).filter(x => x),
        equipmentTypeIds: machinesProp.flatMap(x => x.equipmentType?.id).filter(x => x),
        oemIds: machinesProp.flatMap(x => x.oem?.id).filter(x => x),
        machineModelIds: machinesProp.flatMap(x => x.machineModel?.id).filter(x => x),
        machineUnitIds: machinesProp.flatMap(x => x.machineUnits).filter(x => x),
        plcTypeIds: machinesProp.flatMap(x => x.plcTypes).filter(x => x)
      };
      this.props.hierarchyStore.get(model).then(resp => {
        if (resp.isSuccess) {
          let machines: MachineListItemViewModel[] = [];
          const idNames = resp.item;
          // Get current state to preserve hasAvailablePlcTypes that may have been updated asynchronously
          const currentMachines = this.props.value.machines;
          machines = machinesProp.map((pr, idx) => ({
            cluster: pr.cluster ? this.toItemReference(idNames.clusters.find(x => x.id === pr.cluster.id)) : null,
            equipmentType: pr.equipmentType ? this.toItemReference(idNames.equipmentTypes.find(x => x.id === pr.equipmentType.id)) : null,
            oem: pr.oem ? this.toItemReference(idNames.oems.find(x => x.id === pr.oem.id)) : null,
            machineModel: pr.machineModel ? this.toItemReference(idNames.machineModels.find(x => x.id === pr.machineModel.id)) : null,
            machineUnits: pr.machineUnits,
            plcTypes: pr.plcTypes,
            // Preserve current hasAvailablePlcTypes if it was updated by MultiPlcTypeEditor callback
            hasAvailablePlcTypes: currentMachines[idx]?.hasAvailablePlcTypes ?? pr.hasAvailablePlcTypes,
            machineUnitItems:
              pr.machineUnits && pr.machineUnits.length > 0
                ? idNames.machineUnits.filter(x => pr.machineUnits.includes(x.id)).map(x => this.toItemReference(x))
                : [],
            plcTypeItems:
              pr.plcTypes && pr.plcTypes.length > 0
                ? idNames.plcTypes.filter(x => pr.plcTypes.includes(x.id)).map(x => this.toItemReference(x))
                : []
          }));

          const patternViewModel: PatternMachinesViewModel = { ...this.props.value, machines };
          this.setState({}, () => this.props.onChange(patternViewModel));
        }
      });
    }

    if (this.props.value.pattern && this.props.value.pattern.id) {
      this.setState({ hasPattern: true });
      this.loadPattern(this.props.value.pattern.id).then(pattern => {
        this.setState({ patternRows: pattern?.patternRows });
      });
    }
  }

  private toItemReference = (item: IdNameDto) => {
    if (!item) return null;
    const model: ItemReference = {
      id: item.id,
      title: item.name
    };
    return model;
  };

  private handleOnHasPatternChange = (hasPattern: boolean) => {
    let newValue = { ...this.props.value };
    newValue.pattern = null;
    newValue.machines = !hasPattern
      ? [{ cluster: null, equipmentType: null, oem: null, machineModel: null, machineUnits: [], plcTypes: [] }]
      : [];

    this.setState({ hasPattern: hasPattern });
    this.props.onChange(newValue);
  };

  private handleOnPatternChange = (pattern: ItemReference) => {
    if (!pattern) {
      let value = { ...this.props.value };
      value.pattern = null;
      value.machines = [];

      this.props.onChange(value);
      return;
    }

    this.loadPattern(pattern.id).then(({ patternRows }) => {
      const machines: MachineListItemViewModel[] = this.mapPatternRowsToViewModel(patternRows || []);

      const value: PatternMachinesViewModel = { ...this.props.value, pattern, machines };

      this.setState({ patternRows }, () => this.props.onChange(value));
    });
  };

  private loadPattern = (patternId: string) => this.props.patternsStore.getById(patternId);

  private getClusterMethod() {
    const method = async (search: string) => {
      const filters = isNullOrWhiteSpaces(search) ? [] : [`contains(tolower(${nameof<ClusterDto>('name')}), '${search.toLowerCase()}')`];

      const result = await this.clustersStore.getAllClustersAsync({
        searchQuery: '',
        skip: 0,
        take: 100000,
        orderBy: [{ direction: 'Ascending', field: nameof<ClusterDto>('name'), useProfile: false }],
        filter: filters
      });
      this.setState({ clusters: result });
      return Object.assign({}, result, { items: result.items.map(c => ({ id: c.id, title: c.name })) }) as QueryResult<ItemReference>;
    };
    return method;
  }

  private getEquipmentTypeMethod() {
    const method = async () => {
      const filters: any[] = [{ clusterName: { eq: 'Cigarette Packing' } }];
      filters.push({
        or: [{ name: 'Packer' }, { name: 'Cellophaner / Wrapper' }, { name: 'Tax Stamper' }, { name: 'Cartoner / Parceler' }]
      });

      const result = await this.equipmentTypesStore.getAllAsync({
        searchQuery: '',
        skip: 0,
        take: 100000,
        orderBy: [{ direction: 'Ascending', field: nameof<EquipmentTypeDto>('name'), useProfile: false }],
        filter: filters
      });

      this.setState({ equipmentTypes: result });

      return Object.assign({}, result, {
        items: result.items.map(c => ({ id: c.id, title: c.name, cluster: { id: c.clusterId, title: c.clusterName } }))
      }) as QueryResult<EquipmentTypeItemReference>;
    };
    return method;
  }

  private machineUnitOrPlcTypeHadChanged = (newMachine: MachineListItemViewModel, currentMachine: MachineListItemViewModel): boolean => {
    const newPlc = newMachine?.plcTypes ?? [];
    const curPlc = currentMachine?.plcTypes ?? [];
    const newUnits = newMachine?.machineUnits ?? [];
    const curUnits = currentMachine?.machineUnits ?? [];

    return !customEqualCompareBystringify(newPlc, curPlc) || !customEqualCompareBystringify(newUnits, curUnits);
  };

  canAddAutomaticMachines = (value: MachineListItemViewModel[], currentMachine: MachineListItemViewModel): boolean => {
    const { hasPattern } = this.state;
    let someMachineDeletedCopy = this.state.someMachineDeleted;
    let canAdd: boolean = false;

    if (hasPattern) return canAdd;

    const isOnlyOneMachine = value.length === 1;
    const isMachineEmpty = (m: MachineListItemViewModel) => !m.cluster && !m.equipmentType && !m.oem && !m.machineModel;

    const allMachinesEmpty = value.every(isMachineEmpty);
    const isCurrentMachineUnique = value.every(
      x => x.cluster?.id !== currentMachine.cluster?.id || x.equipmentType?.id !== currentMachine.equipmentType?.id
    );

    if (someMachineDeletedCopy && (isOnlyOneMachine || allMachinesEmpty || isCurrentMachineUnique)) {
      someMachineDeletedCopy = false;
      this.setState({ someMachineDeleted: someMachineDeletedCopy });
    }

    canAdd = !someMachineDeletedCopy;
    return canAdd;
  };

  private handleOnMachineChange = async (index: number, newMachine: MachineListItemViewModel) => {
    let value = { ...this.props.value };
    const currentMachine = value.machines[index];

    value.machines[index] = {
      ...currentMachine,
      cluster: newMachine.cluster !== undefined ? newMachine.cluster : currentMachine.cluster,
      equipmentType: newMachine.equipmentType !== undefined ? newMachine.equipmentType : currentMachine.equipmentType,
      oem: newMachine.oem !== undefined ? newMachine.oem : currentMachine.oem,
      machineModel: newMachine.machineModel !== undefined ? newMachine.machineModel : currentMachine.machineModel,
      plcTypes: newMachine.plcTypes !== undefined ? newMachine.plcTypes : currentMachine.plcTypes,
      machineUnits: newMachine.machineUnits !== undefined ? newMachine.machineUnits : currentMachine.machineUnits,
      hasAvailablePlcTypes:
        newMachine.hasAvailablePlcTypes !== undefined ? newMachine.hasAvailablePlcTypes : currentMachine.hasAvailablePlcTypes
    };

    // Check if there were real changes in cluster or equipment type
    const clusterChanged = newMachine.cluster !== undefined && currentMachine.cluster?.id !== newMachine.cluster?.id;
    const equipmentTypeChanged =
      newMachine.equipmentType !== undefined && currentMachine.equipmentType?.id !== newMachine.equipmentType?.id;

    // Only add machines automatically if there were changes in cluster or equipment type
    if (!clusterChanged && !equipmentTypeChanged) {
      this.props.onChange(value);
      return;
    }

    const canAdd = this.canAddAutomaticMachines(value.machines, currentMachine);

    // If automatic machines cannot be added, exit
    if (!canAdd) {
      this.props.onChange(value);
      return;
    }

    this.setState({ loading: true });

    const clustersQuery = await this.getClusterMethod();
    const clusters = await clustersQuery('Cigarette Packing');
    const autoCluster = clusters.items.map(x => x.id);

    if (newMachine != null && newMachine.cluster != null && autoCluster.includes(newMachine.cluster.id)) {
      const equipmentTypesQuery = await this.getEquipmentTypeMethod();
      const equipmentTypes = await equipmentTypesQuery();
      const todasLasOpciones = equipmentTypes.items.map(x => ({ id: x.id, title: x.title } as ItemReference));

      if (newMachine.equipmentType != null && todasLasOpciones.map(x => x.id).includes(newMachine.equipmentType.id)) {
        const existingEquipmentTypeIds = value.machines.map(x => x.equipmentType?.id).filter(id => id);

        for (let opcion of todasLasOpciones) {
          if (
            !this.machineUnitOrPlcTypeHadChanged(newMachine, currentMachine) &&
            !existingEquipmentTypeIds.includes(opcion.id) &&
            value.machines.length < 4
          ) {
            value.machines.push({
              cluster: newMachine.cluster,
              equipmentType: opcion,
              oem: null,
              machineModel: null,
              machineUnits: [],
              plcTypes: [],
              machineUnitItems: [],
              plcTypeItems: []
            } as MachineListItemViewModel);
          }
        }
      }
    }

    this.setState({ loading: false });
    this.props.onChange(value);
  };

  private handleOnDeleteRow = (index: number) => {
    const { value, onChange } = this.props;
    this.setState({ someMachineDeleted: true });
    onChange({ ...value, machines: value.machines.filter((_, i) => i !== index) });
  };

  private mapPatternRowsToViewModel = (patternRows: PatternRowDto[]) => {
    const machines: MachineListItemViewModel[] = (patternRows || []).map(pr => ({
      cluster: pr.machineRelatedClusterId ? { id: pr.machineRelatedClusterId, title: pr.machineRelatedClusterName } : null,
      equipmentType: pr.equipmentTypeId ? { id: pr.equipmentTypeId, title: pr.equipmentTypeName } : null,
      oem: pr.oemId ? { id: pr.oemId, title: pr.oemName } : null,
      machineModel: pr.machineModelId ? { id: pr.machineModelId, title: pr.machineModelName } : null,
      machineUnits: [],
      plcTypes: []
    }));
    return machines;
  };

  private matchMachineClusteAndEquipment = (): boolean => {
    const { machines } = this.props.value;
    const { hasPattern } = this.state;
    let warningEquipmentTypes: string[] = ['Packer', 'Cellophaner / Wrapper', 'Tax Stamper', 'Cartoner / Parceler'];

    if (hasPattern || machines == null) return false;

    let match = machines.any(x => x != null && x.cluster != null && x.cluster.title === 'Cigarette Packing');
    match = match && machines.any(x => x != null && x.equipmentType != null && warningEquipmentTypes.includes(x.equipmentType.title));

    return match;
  };

  private automaticallyAddedMachines = (): boolean => {
    const { machines } = this.props.value;
    return this.matchMachineClusteAndEquipment() && machines.length !== 1;
  };

  canDelete = () => {
    const { readOnly } = this.props;
    const { machines } = this.props.value;
    const { hasPattern } = this.state;
    const showWarningMessage = this.matchMachineClusteAndEquipment();

    return !readOnly && (hasPattern || (showWarningMessage && machines.length > 1));
  };

  render() {
    const { t, readOnly, patternsStore, loadPatternsOnOpen, showRequired, profession, warningMessageEnabled } = this.props;
    const { pattern, machines } = this.props.value;
    const { hasPattern, patternRows, loading } = this.state;
    const requiredFieldsClassName = showRequired ? 'field required' : 'field';
    const { isBusy } = patternsStore.state;
    const showWarningMessage = warningMessageEnabled && warningMessageEnabled === true && this.matchMachineClusteAndEquipment();
    const isPlcTypeRequired = showRequired && !hasPattern && profession.title === 'Electronic';
    return (
      <>
        <Form.Field className="request-details-tab__mr__pattern-row-btns">
          <p>{t('Pattern')}</p>

          <Button.Group className="mr__pattern-row-btns-wrapper">
            <Button
              attached="left"
              disabled={readOnly}
              className={hasPattern ? 'events-form__btn__positive' : 'events-form__btn__negative'}
              onClick={() => this.handleOnHasPatternChange(true)}
            >
              {t('Yes')}
            </Button>
            <Button
              attached="right"
              disabled={readOnly}
              className={hasPattern ? 'events-form__btn__negative' : 'events-form__btn__positive'}
              onClick={() => this.handleOnHasPatternChange(false)}
            >
              {t('No')}
            </Button>
          </Button.Group>

          {hasPattern && (
            <PatternEditor
              nullable
              clearable
              showOnlyActive
              readOnly={readOnly}
              className="event-details__pattern-dropdown"
              value={pattern}
              onChange={this.handleOnPatternChange}
              loadDataOnOpen={loadPatternsOnOpen}
            />
          )}
        </Form.Field>

        {machines && (
          <>
            <div className="event-details-tab__mr-table scroll">
              <div className={requiredFieldsClassName}>
                <label className="mr-table__column-title">{t('Cluster')}</label>
              </div>
              <div className={requiredFieldsClassName}>
                <label className="mr-table__column-title">{t('Equipment Type')}</label>
              </div>
              <div className={showRequired && !hasPattern ? 'field required' : 'field'}>
                <label className="mr-table__column-title">{t('OEM')}</label>
              </div>
              <div className={showRequired && !hasPattern ? 'field required' : 'field'}>
                <label className="mr-table__column-title">{t('Machine Model')}</label>
              </div>
              <div className="field">
                <label className="mr-table__column-title">{t('Machine Unit')}</label>
              </div>
              <div className={isPlcTypeRequired ? 'field required' : 'field'}>
                <label className="mr-table__column-title" title={isPlcTypeRequired ? t('Required if PLC Types are available') : ''}>
                  {t('PLC Type')}
                </label>
              </div>
              <div className="field">
                <label className="mr-table__column-title text__transparent">X</label>
              </div>
              <Dimmer style={{ zIndex: 999, background: 'rgba(0, 0, 0, 0.4)', position: 'fixed' }} active={loading}>
                <Loader indeterminate>{t('Please wait while respective machinery is being loaded ...')}</Loader>
              </Dimmer>
              {machines.map((machine, index) => {
                // Generate a stable key based on machine IDs to prevent remounting on deletes
                const machineKey =
                  [machine.cluster?.id, machine.equipmentType?.id, machine.oem?.id, machine.machineModel?.id, index]
                    .filter(Boolean)
                    .join('-') || `empty-${index}`;

                return (
                  <MachineListItem
                    key={machineKey}
                    pattern={hasPattern && (patternRows || []).length > index ? patternRows[index] : null}
                    machine={machine}
                    onEditMode={!readOnly && !isBusy}
                    canDelete={this.canDelete()}
                    onChange={value => this.handleOnMachineChange(index, value)}
                    onDeleteClose={() => this.handleOnDeleteRow(index)}
                    automaticallyAddedMachines={this.automaticallyAddedMachines()}
                  />
                );
              })}
            </div>
            {showWarningMessage && (
              <div key={'warnmsgautomaticallyaddedequipment'} className="section">
                <div className="section-item">
                  <Icon name="exclamation triangle" />
                  <p className="event-code">
                    {t(
                      'Please add all respective machinery information if necessary or remove the unrelated ones by clicking the cross icon on the right side of each row.'
                    )}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </>
    );
  }
}

export default withTranslation()(PatternMachines);
